import { useMemo, useEffect } from 'react';
import { AlertTriangle, CreditCard, CalendarDays, Landmark, Store } from 'lucide-react';
import { CardSelectInput } from '@/Components/shared/CardSelectInput';
import { useWarrantyDetails } from '@/features/warranties/hooks/useWarrantyDetails';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useCanMonthlyPayment } from '@/features/societies/hooks/useCanMonthlyPayment';
import type { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import { usePricingInfo } from '@/features/contracts/hooks/usePricingInfo';
import { calculateMonthlyPrice } from '@/features/warranties/services/pricingCalculator';
import { PricingSummaryCard } from '@/features/contracts/components/form-fields/PricingSummaryCard';


/**
 * Vrai si un contrat mensuel commence au-delà de la limite Stripe pour un 1er
 * prélèvement (2 ans, moins un jour de marge). Même règle que l'API, qui refuse alors
 * la création, quel que soit le réglage « 1er prélèvement à la date de début ».
 */
function isStartBeyondFirstChargeLimit(subscriptionDate: string | undefined): boolean {
  if (!subscriptionDate) return false;
  const start = new Date(subscriptionDate);
  if (Number.isNaN(start.getTime())) return false;
  const limit = new Date();
  limit.setUTCMonth(limit.getUTCMonth() + 24);
  limit.setUTCDate(limit.getUTCDate() - 1);
  return start > limit;
}

interface Step4Props {
  formData: ContractFormState;
  updateFormData: (updates: Partial<ContractFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

/**
 * Step 4 : Mode et moyen de paiement (affiché uniquement quand le paiement en ligne est disponible)
 */
export function Step4Payment({ formData, updateFormData, onCanProceedChange, showErrors = false }: Step4Props) {
  const { data: warranty } = useWarrantyDetails(formData.warrantyUuid);
  const { data: config } = useEffectiveConfiguration(formData.societyId);
  const { priceInfo, pricingSummary } = usePricingInfo(formData);

  const hasStripeAccount = !!config?.stripeAccountId;
  const canMonthlyPayment = useCanMonthlyPayment(formData.societyId);
  const hasOnlinePayment = config?.onlinePayment === 2 && hasStripeAccount;

  const paymentOptions = useMemo(() => {
    if (!warranty) return { showSelector: true, forced: null, monthlyBlocked: false };

    const isMonthlyOnly = !warranty.isCashPayment && warranty.isMensualisable;
    const isCashOnly = !warranty.isMensualisable;

    if (!canMonthlyPayment) {
      if (isMonthlyOnly) return { showSelector: false, forced: 1 as const, monthlyBlocked: true };
      if (!isCashOnly && warranty.isMensualisable) return { showSelector: false, forced: 1 as const, monthlyBlocked: true };
    }

    if (isMonthlyOnly) return { showSelector: false, forced: 2 as const, monthlyBlocked: false };
    if (isCashOnly) return { showSelector: false, forced: 1 as const, monthlyBlocked: false };
    return { showSelector: true, forced: null, monthlyBlocked: false };
  }, [warranty, canMonthlyPayment]);

  // Auto-force payment type when warranty dictates it
  useEffect(() => {
    if (paymentOptions.forced && formData.paymentType !== paymentOptions.forced) {
      updateFormData({ paymentType: paymentOptions.forced });
    }
    // Init paymentMethod = 1 (CB) dès que le bloc CB/SEPA est affiché
    // (paymentType en ligne, qu'il soit forcé ou choisi par défaut), pour
    // aligner le state avec l'affichage par défaut "Carte bancaire".
    if (
      formData.paymentType === 1 &&
      hasOnlinePayment &&
      !formData.includedOption &&
      !formData.paymentMethod
    ) {
      updateFormData({ paymentMethod: 1 });
    }
  }, [paymentOptions.forced, hasOnlinePayment, formData.paymentType, formData.includedOption, formData.paymentMethod]);

  // Stripe refuse un 1er prélèvement à plus de 2 ans : contrat mensuel refusé au-delà.
  const monthlyStartBeyondLimit =
    formData.paymentType === 2 && isStartBeyondFirstChargeLimit(formData.subscriptionDate);

  // Validation: paymentType must be set, and if online+cash, paymentMethod must be set
  const canProceed = useMemo(() => {
    if (!formData.paymentType) return false;
    if (formData.paymentType === 1 && hasOnlinePayment && !formData.paymentMethod) return false;
    if (monthlyStartBeyondLimit) return false;
    return true;
  }, [formData.paymentType, formData.paymentMethod, hasOnlinePayment, monthlyStartBeyondLimit]);

  useEffect(() => {
    onCanProceedChange(canProceed);
  }, [canProceed, onCanProceedChange]);

  const hasPrice = Number(formData.sellingPrice) > 0 || Number(formData.monthlyPrice) > 0 || Number(formData.monthlyPriceWithFees) > 0;

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-2 sm:tw-p-4">
      <div className="tw-py-4">
        {/* Titre de la step */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-6">
          <CreditCard className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
          <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-0">Paiement</h3>
        </div>

        {/* Alerte mensualisation non disponible */}
        {paymentOptions.monthlyBlocked && !formData.includedOption && (
          <div className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-mb-6">
            <AlertTriangle className="tw-w-5 tw-h-5 tw-text-amber-500 tw-flex-shrink-0 tw-mt-0.5" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-amber-800 tw-mb-1">Mensualisation non disponible</p>
              <p className="tw-text-xs tw-text-amber-700 tw-mb-0">
                Le compte Stripe de la société n'est pas encore finalisé. La mensualisation sera disponible une fois la configuration Stripe terminée. Le contrat sera créé en paiement comptant.
              </p>
            </div>
          </div>
        )}

        {/* Alerte date de début trop lointaine pour un 1er prélèvement mensuel */}
        {monthlyStartBeyondLimit && (
          <div className="tw-flex tw-items-start tw-gap-3 tw-p-3 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-mb-6">
            <AlertTriangle className="tw-w-5 tw-h-5 tw-text-red-500 tw-flex-shrink-0 tw-mt-0.5" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-red-800 tw-mb-1">Paiement mensualisé impossible</p>
              <p className="tw-text-xs tw-text-red-700 tw-mb-0">
                Le contrat commence dans plus de 2 ans.
              </p>
            </div>
          </div>
        )}

        {/* Bloc mode de paiement (comptant/mensualisé) - masqué si pas de choix */}
        {paymentOptions.showSelector && !formData.includedOption && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-mb-6">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
              Choisissez comment le client règle la garantie : en une fois ou en mensualités étalées sur la durée du contrat
            </p>
            <CardSelectInput
              options={[
                {
                  value: '1',
                  label: hasOnlinePayment ? 'Paiement en ligne' : 'Comptant',
                  description: hasOnlinePayment ? 'Paiement par carte bancaire ou SEPA' : 'Paiement en une seule fois',
                  icon: <CreditCard className="tw-w-5 tw-h-5" />,
                },
                { value: '2', label: 'Paiement mensualisé', description: 'Paiement étalé sur la durée', icon: <CalendarDays className="tw-w-5 tw-h-5" /> },
              ]}
              value={formData.paymentType.toString()}
              onChange={(val) => {
                const newType = parseInt(val, 10) as 1 | 2;
                const updates: Partial<ContractFormState> = {
                  paymentType: newType,
                  paymentMethod: newType === 1 && hasOnlinePayment ? (formData.paymentMethod || 1) : undefined,
                };
                if (newType === 2 && warranty && Number(formData.sellingPrice) > 0) {
                  const baseMonthly = Number(formData.sellingPrice) / warranty.duration;
                  if (warranty.isCashPayment && warranty.isMensualisable) {
                    // isBoth: frais ajoutés, payés par le client
                    updates.monthlyPriceWithFees = calculateMonthlyPrice(baseMonthly, config?.monthlyPlateformFees || 0);
                  } else if (!warranty.isCashPayment && warranty.isMensualisable) {
                    // isMonthlyOnly: frais inclus, payés par le vendeur
                    updates.monthlyPrice = baseMonthly;
                  }
                }
                updateFormData(updates);
              }}
              name="payment-type"
              required
              columns={2}
              tooltip="Paiement en ligne : le client paie immédiatement par CB ou SEPA. Mensualisé : le montant est étalé sur la durée de la garantie"
            />
            {showErrors && !formData.paymentType && (
              <div className="tw-text-red-500 tw-text-sm tw-mt-2">Veuillez sélectionner un mode de paiement</div>
            )}
          </div>
        )}

        {/* Bloc moyen de paiement (CB / SEPA / Point de vente) */}
        {formData.paymentType === 1 && hasOnlinePayment && !formData.includedOption && (
          <div className="tw-bg-affinicar-primary/5 tw-rounded-xl tw-p-6 tw-border-l-4 tw-border-l-grey tw-mb-6">
            <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
              Sélectionnez le moyen de paiement utilisé par le client
            </p>
            <CardSelectInput
              options={[
                { value: '1', label: 'Carte bancaire', description: 'Visa, Mastercard...', icon: <CreditCard className="tw-w-5 tw-h-5" /> },
                { value: '2', label: 'Prélèvement SEPA', description: 'Débit bancaire direct', icon: <Landmark className="tw-w-5 tw-h-5" /> },
                ...(config?.pointOfSalePayment ? [{ value: '3', label: 'En point de vente', description: 'Paiement sur place', icon: <Store className="tw-w-5 tw-h-5" /> }] : []),
              ]}
              value={formData.paymentMethod?.toString() ?? ''}
              onChange={(val) => updateFormData({ paymentMethod: parseInt(val, 10) as 1 | 2 | 3 })}
              name="payment-method"
              required
              columns={config?.pointOfSalePayment ? 3 : 2}
            />
            {showErrors && formData.paymentType === 1 && hasOnlinePayment && !formData.paymentMethod && (
              <div className="tw-text-red-500 tw-text-sm tw-mt-2">Veuillez sélectionner un moyen de paiement</div>
            )}
          </div>
        )}

        {/* Message si paiement forcé (pas de choix) */}
        {!paymentOptions.showSelector && !paymentOptions.monthlyBlocked && !formData.includedOption && (
          <div className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg">
            <CreditCard className="tw-w-5 tw-h-5 tw-text-gray-500" />
            <span className="tw-text-sm tw-text-gray-700">
              {paymentOptions.forced === 2
                ? 'Cette garantie est uniquement disponible en paiement mensualisé.'
                : 'Cette garantie est uniquement disponible en paiement comptant.'}
            </span>
          </div>
        )}

        {/* Récapitulatif tarification */}
        {hasPrice && pricingSummary && warranty && (
          <>
            <div className="tw-h-px tw-bg-affinicar-primary tw-rounded-full tw-my-6" />
            <PricingSummaryCard
              label={priceInfo.label}
              isMonthly={formData.paymentType === 2}
              duration={warranty.duration}
              totalTTC={pricingSummary.totalPriceTTC}
              monthlyClient={priceInfo.clientPaysMonthly}
              monthlySeller={priceInfo.sellerReceivesMonthly}
              feePercentage={priceInfo.feePercentage}
              feeAmountTTC={priceInfo.feeAmountTTC}
            />
          </>
        )}
      </div>
    </div>
  );
}
